import { UTCDateMini } from '@date-fns/utc';
import { captureException } from '@sentry/react';
import {
  addDays,
  differenceInDays,
  endOfDay,
  formatDuration as fnsFormatDuration,
  isSameYear as fnsIsSameYear,
  format,
  intervalToDuration,
  isValid,
  startOfDay,
  startOfISOWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { formatInTimeZone, format as formatTz } from 'date-fns-tz';

import { Timezone } from 'shared/types/dateTime';

export const DATE_FORMAT = 'MMM d, yyyy';
export const SHORT_DATE_FORMAT = 'MM/dd/yyyy';
export const FILTER_DATE_FORMAT = 'MM/dd/yy';

export const API_DATE_FORMAT = 'yyyy-MM-dd';

export const TIME_FORMAT = 'h:mm:ss a';

type DateType = Date | number | string | null;

type FormatDateTimezone = 'utc' | 'local';

type FormatDateOptions = {
  // 'utc' renders date-only backend strings without a local-timezone day shift.
  timezone?: FormatDateTimezone;
  dateFormat?: string;
};

export function formatDate(date?: DateType, options?: FormatDateOptions): string;
/** @deprecated Pass an options object instead: `formatDate(date, { timezone, dateFormat })`. */
export function formatDate(date: DateType | undefined, utc: boolean | undefined, dateFormat?: string): string;
export function formatDate(
  date?: DateType,
  utcOrOptions: boolean | FormatDateOptions = false,
  dateFormat = DATE_FORMAT,
): string {
  const { timezone = 'local', dateFormat: format_ = DATE_FORMAT } =
    typeof utcOrOptions === 'object' ? utcOrOptions : { timezone: utcOrOptions ? 'utc' : 'local', dateFormat };

  if (!date) {
    return '–';
  }

  const parsed = new Date(date);

  if (!isValid(parsed)) {
    return '–';
  }

  try {
    return timezone === 'utc' ? formatInTimeZone(parsed, 'UTC', format_) : format(parsed, format_);
  } catch (error) {
    console.error(error);
    captureException(error);

    return '–';
  }
}

export const formatDateTime = (date?: DateType, dateFormat = DATE_FORMAT, timeFormat = TIME_FORMAT) => {
  if (!date) {
    return '–';
  }

  const fnsDate = new Date(date);

  if (!isValid(fnsDate)) {
    return '–';
  }

  try {
    return formatTz(fnsDate, `${dateFormat}, ${timeFormat} z`, { locale: enUS });
  } catch (error) {
    console.error(error);
    captureException(error);

    return '–';
  }
};

export const formatDateTimeShort = (date?: DateType, dateFormat = DATE_FORMAT) => {
  if (!date) {
    return '–';
  }

  const fnsDate = new Date(date);

  if (!isValid(fnsDate)) {
    return '–';
  }

  try {
    return format(fnsDate, `${dateFormat}, h:mm a`);
  } catch (error) {
    console.error(error);
    captureException(error);

    return '–';
  }
};

function getNow(timezone: Timezone) {
  if (timezone === Timezone.Local) {
    return new Date();
  }

  return new UTCDateMini(new Date());
}

export function getPeriodStart(days: number, timezone: Timezone) {
  return format(addDays(startOfDay(getNow(timezone)), days), API_DATE_FORMAT);
}

export function getPeriodEnd(days: number, timezone: Timezone) {
  return format(addDays(endOfDay(getNow(timezone)), days), API_DATE_FORMAT);
}

export function getWeekStart(timezone: Timezone) {
  return format(startOfISOWeek(getNow(timezone)), API_DATE_FORMAT);
}

export function isSameYear(date1?: DateType, date2?: DateType) {
  return !!date1 && !!date2 && fnsIsSameYear(date1, date2);
}

export const formatDuration = (ms: number) => {
  const duration = intervalToDuration({
    start: 0,
    end: ms,
  });

  const { hours, minutes, seconds } = duration;

  if (!(hours || minutes || seconds)) {
    return 'now';
  }

  return `in ${fnsFormatDuration(duration)}`;
};

export const apiDateDiff = (dateFrom: string, dateTo: string) => {
  return differenceInDays(startOfDay(new UTCDateMini(dateTo)), startOfDay(new UTCDateMini(dateFrom)));
};
